/**
 * P2 · Customer-facing Connect page.
 *
 * Clean, screen-recordable per-channel OAuth entry for the Pinterest
 * Standard review and TikTok audit. Each card:
 *   - shows the current connection state (none / connected / pending
 *     reconnect) for THIS org;
 *   - launches the appropriate ``GET /publish/oauth/{channel}/start``
 *     endpoint, which embeds this org's id into the state parameter;
 *   - surfaces the legal URLs on precipice-health.com that reviewers
 *     need to see in the consent copy.
 *
 * Precipice-specific URLs are the ONLY out-of-tree reference; everything
 * else is per-org data.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

// Legal URLs are public on precipice-health.com; Pinterest Standard &
// TikTok reviewers inspect them inside the consent screens.
const LEGAL = {
  privacy:  "https://precipice-health.com/privacy",
  terms:    "https://precipice-health.com/terms",
  // precipice-health.com/data-deletion is being added on the main
  // domain; until that lands the Resend-powered /api/legal page is
  // the authoritative placeholder.
  deletion: "https://precipice-health.com/data-deletion",
};

const CHANNELS = [
  {
    key: "facebook",
    label: "Facebook + Instagram",
    helper: (
      "Connects your Facebook Page and the Instagram Business account "
      + "it owns. Dev-mode admins keep publishing publicly to their own "
      + "Pages; the composer handles the rest."
    ),
    startPath: "/publish/oauth/facebook/start?intent=create",
    scopes: [
      "pages_manage_posts", "pages_read_engagement",
      "instagram_basic", "instagram_content_publish",
    ],
  },
  {
    key: "pinterest",
    label: "Pinterest",
    helper: (
      "Pin images and videos to one of your boards. On Trial access "
      + "pins are visible only to you until Pinterest grants Standard."
    ),
    startPath: "/publish/oauth/pinterest/start?intent=create",
    scopes: ["boards:read", "pins:read", "pins:write", "user_accounts:read"],
  },
  {
    key: "youtube",
    label: "YouTube",
    helper: (
      "Uploads Shorts and standard videos to your channel. Until "
      + "YouTube finishes the API audit, uploads are forced private."
    ),
    startPath: "/publish/oauth/youtube/start?intent=create",
    scopes: ["youtube.upload", "youtube.readonly"],
  },
  {
    key: "tiktok",
    label: "TikTok",
    helper: (
      "Posts videos and photo carousels through the Content Posting "
      + "API. Until TikTok audit clears, posts are SELF_ONLY."
    ),
    startPath: "/publish/oauth/tiktok/start?intent=create",
    scopes: ["user.info.basic", "video.publish", "video.upload"],
  },
];


export default function BrandConnect() {
  const [connections, setConnections] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyChannel, setBusyChannel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cs, aps] = await Promise.all([
        api.get("/publish/channel-connections"),
        api.get("/platform-approvals").catch(() => ({ data: { approvals: [] } })),
      ]);
      setConnections(cs.data.connections || []);
      setApprovals(aps.data.approvals || []);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function startOAuth(channel, path) {
    setBusyChannel(channel);
    try {
      const r = await api.get(path);
      const url = r.data?.authorize_url || r.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        setError(`No authorize_url returned for ${channel}.`);
      }
    } catch (e) {
      setError(
        `${channel}: ${e?.response?.data?.detail?.message || e.message}`,
      );
    } finally {
      setBusyChannel(null);
    }
  }

  return (
    <div
      data-testid="brandos-connect-page"
      style={{ maxWidth: 920, margin: "0 auto", padding: "24px 20px" }}
    >
      <header style={{ marginBottom: 20 }}>
        <h1
          style={{ fontFamily: "var(--bo-serif)", fontSize: 28,
                   margin: "0 0 6px" }}
        >
          Connect your social accounts
        </h1>
        <p style={{ fontSize: 14, color: "var(--bo-muted)", margin: 0 }}>
          Brand OS publishes only to accounts you connect yourself. Tokens
          are stored encrypted and scoped to your organization only. You
          can disconnect any account at any time from Channel Connections.
        </p>
      </header>

      {error && (
        <div
          data-testid="brandos-connect-error"
          style={{
            padding: 12, borderRadius: 6, background: "#ffecec",
            color: "crimson", marginBottom: 14, fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "grid", gap: 14 }}>
        {CHANNELS.map((ch) => {
          const connected = connections.filter(
            (c) => c.channel === ch.key && c.status === "ACTIVE",
          );
          const approval = approvals.find((a) => a.channel === ch.key)
            || (ch.key === "instagram"
                 ? approvals.find((a) => a.channel === "facebook")
                 : null);
          return (
            <section
              key={ch.key}
              data-testid={`brandos-connect-card-${ch.key}`}
              style={{
                border: "1px solid var(--bo-border, #ddd)",
                borderRadius: 10, padding: 18,
                background: "var(--bo-panel, #fff)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between",
                             alignItems: "flex-start", gap: 20 }}>
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontSize: 18, margin: "0 0 4px",
                               fontFamily: "var(--bo-serif)" }}>
                    {ch.label}
                  </h2>
                  <p style={{ fontSize: 13, color: "var(--bo-muted)",
                               margin: "0 0 10px", lineHeight: 1.5 }}>
                    {ch.helper}
                  </p>
                  <div style={{ fontSize: 11, color: "var(--bo-muted)",
                                 marginBottom: 10 }}
                    data-testid={`brandos-connect-scopes-${ch.key}`}
                  >
                    Scopes: {ch.scopes.join(", ")}
                  </div>
                  {connected.length > 0 && (
                    <div
                      data-testid={`brandos-connect-connected-${ch.key}`}
                      style={{ fontSize: 12, marginBottom: 6 }}
                    >
                      <strong>Connected:</strong>{" "}
                      {connected
                        .map((c) => c.display_name || c.id)
                        .join(" · ")}
                    </div>
                  )}
                  {approval?.requires_private_post && (
                    <div
                      data-testid={`brandos-connect-approval-${ch.key}`}
                      style={{ marginTop: 8, padding: "6px 8px",
                                background: "#fff8e1",
                                borderRadius: 4, fontSize: 12 }}
                    >
                      {approval.banner}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column",
                               gap: 6, alignItems: "flex-end" }}>
                  <button
                    type="button"
                    data-testid={`brandos-connect-start-${ch.key}`}
                    onClick={() => startOAuth(ch.key, ch.startPath)}
                    disabled={busyChannel === ch.key || loading}
                    style={{
                      padding: "8px 16px",
                      background: "var(--bo-accent, #1b4bbf)",
                      color: "#fff", border: 0, borderRadius: 6,
                      cursor: "pointer", fontSize: 13, fontWeight: 600,
                    }}
                  >
                    {connected.length > 0
                      ? (busyChannel === ch.key ? "Opening…" : "Add another")
                      : (busyChannel === ch.key ? "Opening…" : "Connect")}
                  </button>
                  <a
                    href="/brand-os/admin/channel-connections"
                    style={{ fontSize: 11, color: "var(--bo-muted)" }}
                  >
                    Manage →
                  </a>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <footer
        data-testid="brandos-connect-legal"
        style={{ marginTop: 20, fontSize: 11, color: "var(--bo-muted)",
                 lineHeight: 1.6, textAlign: "center" }}
      >
        By connecting an account you agree to the{" "}
        <a href={LEGAL.terms} target="_blank" rel="noreferrer">
          Precipice Health Terms
        </a>{" "}·{" "}
        <a href={LEGAL.privacy} target="_blank" rel="noreferrer">
          Privacy Policy
        </a>{" "}·{" "}
        <a href={LEGAL.deletion} target="_blank" rel="noreferrer">
          Data Deletion
        </a>
        <br />
        Revoke Google access any time at{" "}
        <a
          href="https://myaccount.google.com/permissions"
          target="_blank" rel="noreferrer"
        >
          myaccount.google.com/permissions
        </a>.
      </footer>
    </div>
  );
}
